import styled from 'styled-components';
import { Input, Label, Field } from '@shared/ui/Input';
import type { LogistPostalAddress } from '@entities/logistics';

/**
 * Адрес по элементам, как его требует формат электронной накладной: индекс,
 * регион, район, город, населённый пункт, улица, дом, корпус, квартира.
 *
 * Почему не одна строка: свободный текст формат принимает только комментарием
 * к адресу, а сам адрес хочет разобранным. Пустые части не отправляются —
 * пустых значений формат тоже не допускает, а «дом без корпуса» это норма.
 */

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};
`;

/** Части адреса: ключ модели, подпись, ширина колонки. */
const PARTS: ReadonlyArray<{
  key: keyof LogistPostalAddress;
  label: string;
  placeholder?: string;
}> = [
  { key: 'postal_code', label: 'Индекс', placeholder: '626150' },
  { key: 'region_code', label: 'Код региона', placeholder: '72' },
  { key: 'district', label: 'Район' },
  { key: 'city', label: 'Город' },
  { key: 'village', label: 'Населённый пункт' },
  { key: 'street', label: 'Улица' },
  { key: 'house', label: 'Дом' },
  { key: 'block', label: 'Корпус' },
  { key: 'flat', label: 'Квартира / офис' },
];

interface Props {
  value: LogistPostalAddress;
  onChange: (next: LogistPostalAddress) => void;
  disabled?: boolean;
}

export function AddressFields({ value, onChange, disabled }: Props) {
  return (
    <Grid>
      {PARTS.map((part) => (
        <Field key={part.key}>
          <Label>{part.label}</Label>
          <Input
            value={value[part.key] ?? ''}
            placeholder={part.placeholder}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, [part.key]: e.target.value })}
          />
        </Field>
      ))}
    </Grid>
  );
}

/**
 * Убрать пустые части перед отправкой. `null` — адрес не заполнен вовсе: так
 * его и следует сохранить, иначе в документ уйдёт пустая структура.
 */
export function cleanAddress(address: LogistPostalAddress): LogistPostalAddress | null {
  const cleaned: LogistPostalAddress = {};
  for (const part of PARTS) {
    const raw = address[part.key];
    const trimmed = typeof raw === 'string' ? raw.trim() : '';
    if (trimmed) cleaned[part.key] = trimmed;
  }
  return Object.keys(cleaned).length > 0 ? cleaned : null;
}

/** Короткая строка адреса для таблиц: «626150, Тобольск, ул Панин Бугор, 32А». */
export function formatAddress(address: LogistPostalAddress | null | undefined): string {
  if (!address) return '';
  return [
    address.postal_code,
    address.district,
    address.city,
    address.village,
    address.street,
    address.house,
    address.block,
    address.flat,
  ]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(', ');
}
